import { useState, useEffect } from 'react';
import {
  Sparkles,
  Trophy,
  Users,
  TrendingUp,
  Loader2,
  AlertCircle,
  Check,
  ExternalLink,
  Link2,
  Copy,
  CheckCircle,
  Unlock,
  Lock,
  Fingerprint,
  ShieldCheck,
  ShieldOff,
} from 'lucide-react';
import { adminAPI } from '../../services/adminApi';
import { toast } from 'react-toastify';

const LeadMagnetManager = () => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [processingFree, setProcessingFree] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [fingerprintEnabled, setFingerprintEnabled] = useState(true);
  const [loadingFingerprint, setLoadingFingerprint] = useState(false);

  useEffect(() => {
    loadChallenges();
    loadFingerprintSetting();
  }, []);

  const loadFingerprintSetting = async () => {
    try {
      const response = await adminAPI.getSettings();
      const setting = response.data.find(s => s.key === 'fingerprint_check_enabled');
      if (setting) {
        setFingerprintEnabled(setting.value === true || setting.value === 'true');
      }
    } catch (error) {
      console.error('Error loading fingerprint setting:', error);
    }
  };

  const toggleFingerprintCheck = async () => {
    try {
      setLoadingFingerprint(true);
      const newValue = !fingerprintEnabled;
      await adminAPI.updateSetting('fingerprint_check_enabled', newValue);
      setFingerprintEnabled(newValue);
      toast.success(newValue
        ? 'Fingerprint check enabled - Anonymous users limited to 1 AI evaluation'
        : 'Fingerprint check disabled - Anonymous users can get unlimited AI evaluations'
      );
    } catch (error) {
      console.error('Error updating fingerprint setting:', error);
      toast.error('Failed to update setting');
    } finally {
      setLoadingFingerprint(false);
    }
  };

  const generateTrialUrl = (challengeId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/trial/${challengeId}`;
  };

  const copyTrialUrl = async (challengeId) => {
    const url = generateTrialUrl(challengeId);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(challengeId);
      toast.success('Trial URL copied to clipboard!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast.error('Failed to copy URL');
    }
  };

  const loadChallenges = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getLeadMagnetStatus();
      setChallenges(response.data.challenges || []);
    } catch (error) {
      console.error('Error loading challenges:', error);
      toast.error('Failed to load challenges');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (challengeId, currentStatus) => {
    try {
      setProcessing(challengeId);
      await adminAPI.toggleLeadMagnet(challengeId, !currentStatus);

      // Update local state
      setChallenges(prev => prev.map(c => ({
        ...c,
        is_lead_magnet: c.id === challengeId ? !currentStatus : (currentStatus ? c.is_lead_magnet : false)
      })));

      toast.success(
        !currentStatus
          ? 'Challenge set as lead magnet'
          : 'Lead magnet status removed'
      );
    } catch (error) {
      console.error('Error toggling lead magnet:', error);
      toast.error('Failed to update lead magnet status');
    } finally {
      setProcessing(null);
    }
  };

  const handleToggleFree = async (challengeId, currentStatus) => {
    try {
      setProcessingFree(challengeId);
      await adminAPI.toggleFree(challengeId, !currentStatus);

      // Update local state
      setChallenges(prev => prev.map(c => ({
        ...c,
        is_free: c.id === challengeId ? !currentStatus : c.is_free
      })));

      toast.success(
        !currentStatus
          ? 'Challenge set as free for anonymous users'
          : 'Free access removed'
      );
    } catch (error) {
      console.error('Error toggling free status:', error);
      toast.error('Failed to update free status');
    } finally {
      setProcessingFree(null);
    }
  };

  const currentLeadMagnet = challenges.find(c => c.is_lead_magnet);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-700';
      case 'intermediate': return 'bg-yellow-100 text-yellow-700';
      case 'advanced': return 'bg-orange-100 text-orange-700';
      case 'expert': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Lead Magnet Manager</h2>
        <p className="text-gray-500">
          Configure which challenge appears as the free public challenge for customer acquisition
        </p>
      </div>

      {/* Fingerprint Rate Limit Toggle */}
      <div className={`rounded-xl p-5 border-2 transition-all ${
        fingerprintEnabled
          ? 'bg-green-50 border-green-200'
          : 'bg-orange-50 border-orange-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${
              fingerprintEnabled ? 'bg-green-100' : 'bg-orange-100'
            }`}>
              <Fingerprint className={`w-6 h-6 ${
                fingerprintEnabled ? 'text-green-600' : 'text-orange-600'
              }`} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                Anonymous Rate Limiting
                {fingerprintEnabled ? (
                  <ShieldCheck className="w-4 h-4 text-green-600" />
                ) : (
                  <ShieldOff className="w-4 h-4 text-orange-600" />
                )}
              </h3>
              <p className="text-sm text-gray-600">
                {fingerprintEnabled
                  ? 'Enabled: Each anonymous user gets 1 free AI evaluation (uses browser fingerprinting)'
                  : 'Disabled: Anonymous users can get unlimited AI evaluations (useful for testing)'
                }
              </p>
            </div>
          </div>
          <button
            onClick={toggleFingerprintCheck}
            disabled={loadingFingerprint}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              fingerprintEnabled ? 'bg-green-500' : 'bg-gray-300'
            } ${loadingFingerprint ? 'opacity-50' : ''}`}
          >
            {loadingFingerprint ? (
              <Loader2 className="w-4 h-4 animate-spin absolute top-1.5 left-1.5 text-white" />
            ) : (
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                fingerprintEnabled ? 'translate-x-8' : 'translate-x-1'
              }`} />
            )}
          </button>
        </div>
      </div>

      {/* Current Lead Magnet Status */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white/20 rounded-xl">
            <Sparkles className="w-8 h-8" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-1">Current Lead Magnet</h3>
            {currentLeadMagnet ? (
              <div>
                <p className="text-indigo-100 flex items-center gap-2">
                  <Trophy className="w-4 h-4" />
                  {currentLeadMagnet.title}
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    currentLeadMagnet.difficulty === 'beginner' ? 'bg-green-400/30' :
                    currentLeadMagnet.difficulty === 'intermediate' ? 'bg-yellow-400/30' :
                    'bg-orange-400/30'
                  }`}>
                    {currentLeadMagnet.difficulty}
                  </span>
                </p>
                <a
                  href="/try"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-2 text-sm text-white/80 hover:text-white"
                >
                  View public page
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            ) : (
              <p className="text-indigo-100">
                No lead magnet configured. Select a challenge below.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">How Lead Magnet Works:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Only one challenge can be the lead magnet at a time</li>
              <li>Anonymous users can design and get 1 free AI evaluation</li>
              <li>Partial feedback is shown (2/5 criteria visible)</li>
              <li>Leaderboard is blurred to encourage sign-up</li>
              <li>Attempts are tracked for social proof counters</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Challenge List */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-800">
            Available Challenges ({challenges.length})
          </h3>
        </div>

        <div className="divide-y">
          {challenges.map((challenge) => (
            <div
              key={challenge.id}
              className={`px-6 py-4 flex items-center justify-between transition-colors ${
                challenge.is_lead_magnet ? 'bg-indigo-50' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-lg ${
                  challenge.is_lead_magnet
                    ? 'bg-indigo-100'
                    : 'bg-gray-100'
                }`}>
                  <Trophy className={`w-5 h-5 ${
                    challenge.is_lead_magnet
                      ? 'text-indigo-600'
                      : 'text-gray-400'
                  }`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {challenge.title}
                    </span>
                    {challenge.is_lead_magnet && (
                      <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Lead Magnet
                      </span>
                    )}
                    {challenge.is_free && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                        <Unlock className="w-3 h-3" />
                        Free
                      </span>
                    )}
                  </div>
                  <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${getDifficultyColor(challenge.difficulty)}`}>
                    {challenge.difficulty}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Copy Trial URL Button */}
                <button
                  onClick={() => copyTrialUrl(challenge.id)}
                  className="px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200"
                  title={`Copy trial URL: ${generateTrialUrl(challenge.id)}`}
                >
                  {copiedId === challenge.id ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-green-600">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Link2 className="w-4 h-4" />
                      Copy Trial URL
                    </>
                  )}
                </button>

                {/* Set as Free Button */}
                <button
                  onClick={() => handleToggleFree(challenge.id, challenge.is_free)}
                  disabled={processingFree === challenge.id}
                  className={`px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    challenge.is_free
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } disabled:opacity-50`}
                  title={challenge.is_free ? 'Remove free access' : 'Set as free for anonymous users'}
                >
                  {processingFree === challenge.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : challenge.is_free ? (
                    <>
                      <Unlock className="w-4 h-4" />
                      Free
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      Set Free
                    </>
                  )}
                </button>

                {/* Set as Lead Magnet Button */}
                <button
                  onClick={() => handleToggle(challenge.id, challenge.is_lead_magnet)}
                  disabled={processing === challenge.id}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    challenge.is_lead_magnet
                      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  } disabled:opacity-50`}
                >
                  {processing === challenge.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : challenge.is_lead_magnet ? (
                    <>
                      <Check className="w-4 h-4" />
                      Active
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Set as Lead Magnet
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <p className="font-medium text-gray-700 mb-2">Tips for Choosing a Lead Magnet:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Beginner-friendly:</strong> Choose a challenge that's approachable for newcomers</li>
          <li><strong>Popular topic:</strong> URL Shortener, Chat App, or E-commerce are great choices</li>
          <li><strong>Quick to complete:</strong> Ideally under 30 minutes estimated time</li>
          <li><strong>Has reference solution:</strong> Ensure there's a good solution for comparison</li>
        </ul>
      </div>
    </div>
  );
};

export default LeadMagnetManager;
